/** Match-only workload instructions. Enforced by the outdoor planner. */
export interface PlayerParticipation {
  version: 1;
  players: Record<string, { max_playing_time_percent: number }>;
}

export function playerWorkloadPercent(settings: PlayerParticipation | null | undefined, playerId: string): number {
  return settings?.players[playerId]?.max_playing_time_percent ?? 100;
}

export function withPlayerWorkload(
  settings: PlayerParticipation | null | undefined,
  playerId: string,
  percent: number,
): PlayerParticipation | null {
  if (!playerId.trim() || !Number.isInteger(percent) || percent < 1 || percent > 100) {
    throw new Error('Invalid player workload');
  }
  const players = { ...settings?.players };
  if (percent === 100) delete players[playerId];
  else players[playerId] = { max_playing_time_percent: percent };
  return Object.keys(players).length ? { version: 1, players } : null;
}

export function hasLimitedPlayers(settings: PlayerParticipation | null | undefined): boolean {
  return Object.values(settings?.players ?? {}).some(player => player.max_playing_time_percent < 100);
}

export function workloadMinutes(matchSeconds: number, percent: number): number {
  return Math.floor(matchSeconds * percent / 100) / 60;
}

/** Refuse a successful-looking response from a server that ignored this field. */
export function assertParticipationSaved(response: unknown, expected: PlayerParticipation | null): void {
  if (!response || typeof response !== 'object' || !('player_participation' in response)) {
    throw new Error('The server did not save player participation');
  }
  const saved = response.player_participation as PlayerParticipation | null;
  if (expected === null && saved === null) return;
  if (!saved || !expected || saved.version !== expected.version
      || Object.keys(saved.players ?? {}).length !== Object.keys(expected.players).length
      || Object.entries(expected.players).some(([id, player]) =>
        saved.players?.[id]?.max_playing_time_percent !== player.max_playing_time_percent)) {
    throw new Error('The server did not save player participation');
  }
}

export type SubstitutionLimits = { team?: number; lines?: Record<string, number> };

export function strategySubstitutionLimits(raw: unknown): SubstitutionLimits {
  const strategy = record(raw);
  if (strategy.level === 'advanced') {
    const lines = Object.fromEntries(Object.entries(record(record(strategy.advancedRules).lines))
      .filter(([, rule]) => record(rule).maxSubstitutionsPerMoment != null)
      .map(([key, rule]) => [key, record(rule).maxSubstitutionsPerMoment as number]));
    return Object.keys(lines).length ? { lines } : {};
  }
  return strategy.maxSubstitutionsPerMoment != null ? { team: strategy.maxSubstitutionsPerMoment } : {};
}

export interface WorkloadContract {
  substitution_limits?: SubstitutionLimits;
  version: 1;
  quarter_seconds: number;
  quarters: number;
  starts: number[];
  context: Record<string, unknown>;
  basic?: { lines: Record<string, string[]>; assignments: Record<string, string>; locked: Record<string, string>; rhythm: string };
  advanced?: { strategy: unknown; lines: Record<string, string[]>; locked: Record<string, string> };
}

type MatchWorkloadSource = {
  preparation_settings?: { strategy?: unknown } | null;
  player_participation?: PlayerParticipation | null;
  lineup?: unknown;
  reserve_players?: unknown;
  unavailable_players?: unknown;
  standin_players?: unknown;
  substitution_schedule?: unknown;
  formation_key?: string | null;
  field?: string | null;
};
const record = (value: unknown): Record<string, any> => value && typeof value === 'object' ? value as Record<string, any> : {};
const id = (value: unknown): string | undefined => typeof value === 'string' ? value : record(value).id;
export function matchWorkloadContext(match: MatchWorkloadSource): Record<string, unknown> {
  const lineup = Array.isArray(match.lineup) ? Object.fromEntries(match.lineup.map(p => [p.position || p.assignedPosition, p])) : record(match.lineup);
  const overrides: Record<string, unknown> = {};
  [...Object.values(lineup), ...(Array.isArray(match.reserve_players) ? match.reserve_players : [])].forEach(raw => {
    const player = record(raw);
    if (player.matchPositionOverride) overrides[player.id] = {
      variant: player.positionPreferenceVariant || 'outdoor', preferences: player.positionPreferencesByVariant || {},
    };
  });
  return {
    lineup: Object.fromEntries(Object.entries(lineup).filter(([,value]) => id(value)).map(([key,value]) => [key,id(value)])),
    reserves: [...new Set(Array.isArray(match.reserve_players) ? match.reserve_players.map(id).filter(Boolean) : [])].sort(),
    unavailable: [...new Set(Array.isArray(match.unavailable_players) ? match.unavailable_players : [])].sort(),
    standins: [...new Set(Array.isArray(match.standin_players) ? match.standin_players.map(id).filter(Boolean) : [])].sort(),
    formation_key: match.formation_key || '', field: match.field || '',
    limits: Object.fromEntries(Object.entries(match.player_participation?.players || {}).filter(([,v]) => v.max_playing_time_percent < 100).map(([key,v]) => [key,v.max_playing_time_percent])),
    overrides,
  };
}
export function makeWorkloadContract(match: MatchWorkloadSource, quarterSeconds: number, quarters: number, times: number[], force = false): WorkloadContract | undefined {
  const substitution_limits = strategySubstitutionLimits(match.preparation_settings?.strategy);
  // Replacing a certified plan still needs a fresh context after the last cap is removed.
  return (force || Object.keys(substitution_limits).length > 0 || hasLimitedPlayers(match.player_participation) || getScheduleWorkload(match.substitution_schedule))
    ? { version: 1, quarter_seconds: quarterSeconds, quarters, starts: [0, ...times], context: matchWorkloadContext(match), substitution_limits }
    : undefined;
}
export function getScheduleWorkload(schedule: unknown): WorkloadContract | undefined {
  const value = record(record(schedule)._provenance).workload;
  return value?.version === 1 && Array.isArray(value.starts) ? value as WorkloadContract : undefined;
}
export function schedulePlayingSeconds(schedule: Record<string, unknown>, contract: WorkloadContract): Record<string, number> {
  const seconds: Record<string, number> = {};
  Object.entries(schedule).forEach(([key, value]) => {
    const slot = /^(.+)-(\d+)-(\d+)$/.exec(key);
    const playerId = id(value);
    if (!slot || slot[1].startsWith('reserve') || !playerId) return;
    const index = Number(slot[3]);
    seconds[playerId] = (seconds[playerId] || 0) + ((contract.starts[index + 1] ?? contract.quarter_seconds) - contract.starts[index]);
  });
  return seconds;
}
export function assertScheduleWorkload(schedule: Record<string, unknown>, contract: WorkloadContract): void {
  assertSubstitutionLimits(schedule, contract);
  if (contract.basic) {
    const { lines, assignments, locked } = contract.basic;
    let previous: Record<string, string> | undefined;
    for (let quarter = 1; quarter <= contract.quarters; quarter++) {
      for (let slot = 0; slot < contract.starts.length; slot++) {
        const current: Record<string, string> = {};
        for (const [line, positions] of Object.entries(lines)) for (const position of positions) {
          const playerId = id(schedule[`${position}-${quarter}-${slot}`]);
          if (!playerId || assignments[playerId] !== line || current[playerId]) throw new Error('Basis: iedere speler moet in de eigen linie staan, op één plek.');
          if (previous?.[playerId] && previous[playerId] !== position) throw new Error('Basis: een andere plek mag alleen na een bankbeurt.');
          if (locked[position] && locked[position] !== playerId) throw new Error('Basis: een vergrendelde plek is gewijzigd.');
          current[playerId] = position;
        }
        previous = current;
      }
    }
  }
  const planned = schedulePlayingSeconds(schedule, contract);
  const limits = record(contract.context.limits);
  for (const [playerId, percent] of Object.entries(limits)) {
    const max = Math.floor(contract.quarter_seconds * contract.quarters * Number(percent) / 100);
    if ((planned[playerId] || 0) > max) throw new Error(`Maximale speeltijd overschreden: ${Math.round(planned[playerId] / 60 * 10) / 10} / ${max / 60} min (${playerId}).`);
  }
}

/** Validate actual bench exchanges; quarter starts are normal transitions. */
export function assertSubstitutionLimits(schedule: Record<string, unknown>, contract: WorkloadContract): void {
  const limits = contract.substitution_limits ?? {};
  if (!Object.keys(limits).length) return;
  const positions = Object.keys(record(contract.context.lineup));
  const groups: [string, string[], number][] = limits.team != null
    ? [['team', positions, limits.team]]
    : Object.entries(limits.lines ?? {}).map(([line, cap]) => [line, contract.advanced?.lines[line] ?? [], cap]);
  if (!positions.length || groups.some(([, members, cap]) => !members.length || !Number.isInteger(cap) || cap < 1 || cap > 16)) {
    throw new Error('Ongeldige wisselgrens / Invalid substitution limit.');
  }
  const roster = new Set([...Object.values(record(contract.context.lineup)), ...(contract.context.reserves as string[] ?? [])]);
  let previous: Record<string, string> | undefined;
  for (let quarter = 1; quarter <= contract.quarters; quarter++) {
    for (let slot = 0; slot < contract.starts.length; slot++) {
      const current: Record<string, string> = {};
      for (const pos of positions) {
        const pid = id(schedule[`${pos}-${quarter}-${slot}`]);
        if (!pid || !roster.has(pid)) throw new Error('Onvolledig schema / Incomplete schedule.');
        current[pos] = pid;
      }
      const after = new Set(Object.values(current));
      if (after.size !== positions.length) throw new Error('Dubbele speler / Duplicate player.');
      if (previous) {
        const before = new Set(Object.values(previous));
        for (const [line, members, cap] of groups) {
          const incoming = members.filter(pos => !before.has(current[pos])).length;
          const outgoing = members.filter(pos => !after.has(previous![pos])).length;
          if (Math.max(incoming, outgoing) > cap) {
            throw new Error(`${line}: maximaal ${cap} wissels per moment, ook tussen kwarten / at most ${cap} substitutions per moment, including quarter transitions.`);
          }
        }
      }
      previous = current;
    }
  }
}
